import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './WaitlistDialog.css';

/* The early-access gate.
 *
 * Public signup is closed until launch, so every "create an account" call to
 * action opens this instead. It collects an email address and nothing else
 * that is not volunteered.
 *
 * The landing page already promised a waitlist — "Join the waitlist. Be first
 * to the table when the gates open" — while its button went to the login page.
 * This is the thing that copy was describing.
 */
export default function WaitlistDialog({ open, onClose, source = 'unknown' }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | joined
  const [error, setError] = useState('');

  const emailRef = useRef(null);
  const panelRef = useRef(null);
  const returnFocusTo = useRef(null);

  const API = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

  /* Escape closes it, and focus moves into the dialog on open and back to
   * whatever opened it on close — otherwise a keyboard user is dropped at the
   * top of the document every time. */
  useEffect(() => {
    if (!open) return;

    returnFocusTo.current = document.activeElement;
    emailRef.current?.focus();

    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);

    // The page behind must not scroll while a modal is over it.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
      returnFocusTo.current?.focus?.();
    };
  }, [open, onClose]);

  // Reset when it reopens, so a previous "you're on the list" does not greet
  // someone adding a second address.
  useEffect(() => {
    if (open) { setState('idle'); setError(''); }
  }, [open]);

  /* Keeps Tab inside the dialog. Short enough to do by hand — a focus-trap
   * dependency for one dialog is not worth the bytes. */
  const onKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    const focusable = panelRef.current?.querySelectorAll(
      'button, input, [href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable?.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setState('sending');

    try {
      const res = await fetch(`${API}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), source }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Something went wrong');
      setState('joined');
    } catch (err) {
      setError(err.message);
      setState('idle');
    }
  };

  if (!open) return null;

  return (
    <div className="wl-overlay" onClick={onClose}>
      <div
        className="wl-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wl-title"
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <button className="wl-close" onClick={onClose} aria-label="Close">✕</button>

        {state === 'joined' ? (
          <>
            <h2 className="wl-title" id="wl-title">You're on the list</h2>
            <p className="wl-blurb">
              We'll email {email.trim()} when a seat opens up. Nothing else — no
              newsletter, no passing your address on.
            </p>
            <button className="wl-submit" onClick={onClose}>Close</button>
          </>
        ) : (
          <>
            <p className="wl-eyebrow">Early access beta</p>
            <h2 className="wl-title" id="wl-title">The tables aren't open yet</h2>
            <p className="wl-blurb">
              Triad-Ha is in closed beta. Leave your email and we'll tell you the
              moment there's a seat for you.
            </p>

            {error && <div className="wl-error" role="alert">{error}</div>}

            <form className="wl-form" onSubmit={submit}>
              <div className="wl-field">
                <label htmlFor="wl-email">Email</label>
                <input
                  id="wl-email" ref={emailRef}
                  type="email" autoComplete="email" required
                  placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="wl-field">
                <label htmlFor="wl-name">Name <span className="wl-optional">optional</span></label>
                <input
                  id="wl-name"
                  type="text" autoComplete="name" maxLength={80}
                  placeholder="What we should call you"
                  value={name} onChange={(e) => setName(e.target.value)}
                />
              </div>

              <button type="submit" className="wl-submit" disabled={state === 'sending'}>
                {state === 'sending' ? 'Adding you…' : 'Join the waitlist'}
              </button>
            </form>

            {/* True from both places this opens: from the landing page it
                navigates, and from the login page it closes the dialog and
                reveals the form behind it. */}
            <p className="wl-footnote">
              Already have an account?{' '}
              <Link to="/login" onClick={onClose}>Sign in</Link> — the beta is
              already open to you.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
